import "@event-calendar/core/index.css";
import React from "react";
import { renderToStaticMarkup } from "react-dom/server.browser";
import { useLoaderData } from "react-router";
import {
  createCalendar,
  ResourceTimeGrid,
  Interaction,
  destroyCalendar,
} from "@event-calendar/core";
import * as api from "../services/api";
import {
  Select,
  MenuItem,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  RadioGroup,
  CircularProgress,
  Typography,
  Chip,
  useTheme,
  IconButton,
} from "@mui/material";
import * as f from "../utils/formatter";
import * as ws from "../utils/ws";
import { useTranslation } from "react-i18next";
import * as d from "shared/src/date.mjs";
import { groupBy, indexBy, throttle } from "shared/src/util.mjs";
import { appointmentStep, NO_SHOW_M, status } from "shared/src/appointment.mjs";
import { THROTTLE_REFRESH } from "../constants";
import { errorsFromApi, FormErrorList } from "../form";
import {
  appointmentBackground,
  createClickHandler,
  appointmentColor,
  appointmentInfoColor,
  TagList,
  adhocWaitingTimesIcons,
  rawWaitingTimes,
  waitingTimesIcons,
  waitingInfoStatuses,
  bufferService,
} from "../components/calendar";
import { animationInterval } from "../utils/util";
import { createUrlString } from "../url";
import {
  currentLocationFromRequest,
  useCurrentLocation,
} from "../components/LocationSelector";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChairOffice } from "@fortawesome/pro-solid-svg-icons";
import { faCircleCheck, faUser } from "@fortawesome/free-solid-svg-icons";
import ServiceTag from "../components/ServiceTag.jsx";
import { time24 } from "../utils/formatter";
import { adhocTypes } from "../components/reservationDialog/NewAdhocDialog";
import { dateToDay } from "shared/src/calendar.mjs";
import { NowProvider } from "../components/now";
import {
  duration,
  employeeStatusToLabel,
  getAppointmentsByCalendarId,
  getAssignableRoles,
  serviceFormatList,
} from "../components/resource-list/fn.mjs";
import { useNurseOptionsGrouped } from "../components/resource-list/hooks.mjs";
import NurseGroup from "../components/resource-list/NurseGroup";
import LeftMargin from "../components/resource-list/LeftMargin";
import ResourceList from "../components/resource-list/ResourceList";
import { AppointmentDetailDialog } from "../components/calendar/AppointmentDetailDialog";
import { faXmark } from "@fortawesome/pro-light-svg-icons";
import VirtualCareTag from "../components/VirtualCareTag.jsx";

const eventType = {
  APPOINTMENT: "appointment",
  BLOCATION: "blocation",
  BUFFER: "buffer",
};

/**
 * @typedef TreatingInfo
 * @property {number} remaining
 * @property {number} progress
 * @property {number} time
 *
 * @typedef EventCalendar
 * @property {() => Event[]} getEvents
 * @property {(e: Event) => EventCalendar} updateEvent
 *
 * @typedef LocationService
 * @property {number} real_duration
 *
 * @typedef Service
 * @property {string} name
 * @property {LocationService} locationService
 *
 * @typedef Client
 * @property {string} first_name
 * @property {string} last_name
 *
 * @typedef Audit
 * @property {created_at} string
 * @property {object} changed_fields
 *
 * @typedef AuditConnection
 * @property {Audit[]} data
 *
 * @typedef Employee
 * @property {string} id
 * @property {string} first_name
 * @property {string} last_name
 *
 * @typedef Appointment
 * @property {string} start
 * @property {string} end
 * @property {number} status_id
 * @property {string} calendar_id
 * @property {string} [actual_calendar_id]
 * @property {Service} service
 * @property {Client} client
 * @property {AuditConnection} audit
 * @property {Employee} [employee]
 *
 * @typedef Calendar
 * @property {string} id
 * @property {string} title
 * @property {string} location_id
 *
 * @typedef Resource
 * @property {Calendar} extendedProps
 *
 * @typedef Event
 * @property {Date} start
 * @property {Date} end
 * @property {'auto' | 'background'} display
 * @property {Appointment} extendedProps
 *
 * @typedef ResourceStatusEmpty
 * @property {'empty'} type
 *
 * @typedef ResourceStatusLocked
 * @property {'locked'} type
 *
 * @typedef ResourceStatusWaiting
 * @property {'waiting'} type
 * @property {Appointment} appointment
 *
 * @typedef ResourceStatusTreating
 * @property {'treating'} type
 * @property {Appointment} appointment
 *
 * @typedef {ResourceStatusEmpty | ResourceStatusWaiting | ResourceStatusTreating | ResourceStatusLocked} ResourceStatus
 *
 * @typedef AssignCalendar
 * @property {string[]} appointment_ids
 * @property {Calendar} actual_calendar
 *
 * @typedef Buffer
 * @property {string} calendar_id
 * @property {string} from
 * @property {string} to
 */

let dragging = false;
let overInput = false;
let reservationOverlay = false;

export async function loader({ request }) {
  const location_id = currentLocationFromRequest(request);

  return api.loadFloorManagerCalendarData(
    {
      location_id: location_id,
      calendarFilter: {
        visible: { eq: true },
        location_id: { eq: location_id },
      },
      day: d.toDateString(new Date()),
    },
    api.contextFromRequest(request),
  );
}

function ChildrenEventContent({ event, resources, appointmentsByCalendarId }) {
  const children = event.extendedProps.children;
  if (children == null) {
    return null;
  }

  return children.map((c) => {
    return (
      <div
        key={c.id}
        className="appointment-draggable"
        data-appointment-id={c.id}
        data-appointment-group-id={c.group_id}
        data-service={JSON.stringify(c.service)}
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          position: "relative",
          background: appointmentBackground(c),
        }}
        draggable={isAppointmentDraggable(c)}
      >
        <div style={{ padding: "var(--app-calendar-event-padding)" }}>
          <AssignedEvent
            resources={resources}
            appointment={c}
            appointmentsByCalendarId={appointmentsByCalendarId}
          />
          <ReservationOverlay appointment={c} />

          <ServiceTag service={c.service} />
        </div>
        <hr style={{ width: "100%", margin: 0, borderColor: "#B2C6DE" }} />
      </div>
    );
  });
}

function EventContent({ event, now }) {
  const appointment = event.extendedProps;
  const times = waitingTimesIcons(rawWaitingTimes(appointment, now));
  const service = appointment.service;

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: "0.2rem" }}>
        <VirtualCareTag client={appointment.client} />
        <div
          style={{
            color: "var(--mui-palette-_components-heading-color)",
            fontWeight: 700,
          }}
          className="single-line"
        >
          {f.fullName(appointment.client)}
        </div>
      </div>
      <ServiceTag service={service} />
      <div
        style={{
          color: appointmentInfoColor(appointment),
          fontWeight: 700,
        }}
      >
        {f.time24(appointment.start)}-{f.time24(appointment.end)} (
        {f.minutesToDuration(duration(event.start, event.end))})
      </div>
      <div style={{ fontWeight: 700 }}>
        {times?.checked_in_minutes}&nbsp;
        {times?.waiting_minutes}
      </div>
      {appointment.group_id != null &&
        appointment.status_id === status.WAITING && (
          <div>
            <input
              name="appointment[]"
              data-appointment-id={appointment.id}
              data-appointment-group-id={appointment.group_id}
              type="checkbox"
              defaultChecked={selectedAppointments.appointments.has(
                appointment.id,
              )}
            />
          </div>
        )}
    </>
  );
}

function AssignedEventPosition({ position }) {
  if (position == null || position > 1) {
    return null;
  }

  const active = {
    backgroundColor: "#fff",
    borderColor: "#fff",
    borderStyle: "solid",
    borderWidth: "1.8px",
  };
  const inactive = {
    borderColor: "#fff",
    borderStyle: "solid",
    borderWidth: "1.8px",
  };

  return (
    <div
      style={{
        height: "1em",
        width: "0.75em",
        display: "flex",
        flexDirection: "column",
        gap: "10%",
      }}
    >
      <div
        style={{ flexGrow: 1, ...(position === 0 ? active : inactive) }}
      ></div>
      <div
        style={{ flexGrow: 1, ...(position === 1 ? active : inactive) }}
      ></div>
    </div>
  );
}

/**
 * @param {Record<string, Calendar>} resources
 * @param {Appointment} appointment
 * @param {Record<string, Appointment[]} appointmentsByCalendarId
 */
function AssignedEvent({ resources, appointment, appointmentsByCalendarId }) {
  if (
    appointment.parent_id != null &&
    [status.TREATED, status.FINISHED].includes(appointment.status_id)
  ) {
    return (
      <div className="calendar-event--assigned single-line">
        <FontAwesomeIcon icon={faCircleCheck} />
      </div>
    );
  }

  if (appointment.actual_calendar_id == null) {
    return null;
  }

  const assignedResource = resources[appointment.actual_calendar_id];

  const idx = appointmentsByCalendarId[
    appointment.actual_calendar_id
  ]?.findIndex((a) => a.id === appointment.id);

  return (
    <div className="calendar-event--assigned single-line">
      <AssignedEventPosition position={idx} />
      &nbsp;
      {assignedResource?.title}
    </div>
  );
}

function isAppointmentDraggable(appointment) {
  return (
    status.WAITING === appointment.status_id &&
    appointment.actual_calendar_id == null
  );
}

function BlocationContent({ blocation }) {
  return (
    <>
      <div style={{ fontWeight: 700 }}>{blocation.title}</div>
    </>
  );
}

function BufferContent() {
  return (
    <>
      <ServiceTag service={bufferService} />
    </>
  );
}

function ReservationOverlay({ appointment }) {
  if (appointment.reservation_id == null) {
    return null;
  }

  return (
    <div
      className={`calendar-event--reservation-overlay calendar-event--reservation-overlay--${appointment.reservation_id} hidden`}
    ></div>
  );
}

function renderAppointment(
  cache,
  appointment,
  info,
  resourcesById,
  appointmentsByCalendarId,
  now,
) {
  const cached = cache[appointment.id];
  if (
    cached == null ||
    cached.appointment !== appointment ||
    waitingInfoStatuses.has(appointment.status_id)
  ) {
    const html = renderToStaticMarkup(
      <div style={{ display: "flex", height: "100%" }}>
        <TagList appointments={[appointment]} />
        <div
          className="calendar-event"
          style={{ borderColor: appointmentColor(appointment) }}
        >
          <ChildrenEventContent
            event={info.event}
            resources={resourcesById}
            appointmentsByCalendarId={appointmentsByCalendarId}
          />
          <div
            className="calendar-event-parent appointment-draggable"
            data-appointment-id={appointment.id}
            data-appointment-group-id={appointment.group_id}
            data-service={JSON.stringify(appointment.service)}
            style={{
              padding: "var(--app-calendar-event-padding)",
              position: "relative",
              background: appointmentBackground(appointment),
            }}
            draggable={isAppointmentDraggable(appointment)}
          >
            <AssignedEvent
              resources={resourcesById}
              appointment={appointment}
              appointmentsByCalendarId={appointmentsByCalendarId}
            />
            <ReservationOverlay appointment={appointment} />
            <EventContent event={info.event} now={now} />
          </div>
        </div>
      </div>,
    );

    cache[appointment.id] = { appointment, html };
  }

  return cache[appointment.id].html;
}

/**
 * @param {Record<string, Calendar>} resourcesById
 * @param {Record<string, Appointment[]} appointmentsByCalendarId,
 * @param {Date} now
 */
function createEventContentFn(
  cache,
  resourcesById,
  appointmentsByCalendarId,
  now,
) {
  /**
   * @param {object} info
   * @param {Event} info.event
   */
  return function (info) {
    switch (info.event.title) {
      case eventType.APPOINTMENT: {
        const appointment = info.event.extendedProps;

        if (appointment.parent_id != null) {
          return {
            html: renderToStaticMarkup(
              <div style={{ display: "flex", height: "100%" }}>
                <div
                  className="calendar-event"
                  style={{
                    background: appointmentBackground(appointment),
                    borderColor: appointmentColor(appointment),
                  }}
                >
                  <ServiceTag service={appointment.service} />
                </div>
              </div>,
            ),
          };
        }

        return {
          html: renderAppointment(
            cache,
            appointment,
            info,
            resourcesById,
            appointmentsByCalendarId,
            now,
          ),
        };
      }
      case eventType.BLOCATION: {
        const blocation = info.event.extendedProps;

        return {
          html: renderToStaticMarkup(
            <div style={{ display: "flex", height: "100%" }}>
              <div
                className="calendar-event"
                style={{
                  background: "#fff",
                  borderColor: appointmentColor(blocation),
                  color: "var(--mui-palette-_components-heading-color)",
                  padding: "var(--app-calendar-event-padding)",
                }}
              >
                <BlocationContent blocation={blocation} />
              </div>
            </div>,
          ),
        };
      }
      case eventType.BUFFER: {
        return {
          html: renderToStaticMarkup(
            <div style={{ display: "flex", height: "100%" }}>
              <div
                className="calendar-event"
                style={{
                  background: "#fff",
                  borderColor: appointmentColor({ service: bufferService }),
                  color: "var(--mui-palette-_components-heading-color)",
                  padding: "var(--app-calendar-event-padding)",
                }}
              >
                <BufferContent />
              </div>
            </div>,
          ),
        };
      }
    }
  };
}

const interestingStatuses = new Set([
  status.BOOKED,
  status.CONFIRMED,
  status.WAITING,
  status.TREATING,
  status.ASSIGNED,
  status.TREATED,
]);

function dissoc(set, v) {
  const s = new Set(set);
  s.delete(v);

  return s;
}

/**
 * @param {Date} start
 * @param {Date} end
 * @param {string} location_id
 */
function fetchAppointments(start, end, location_id) {
  const bookedSince = new Date();
  bookedSince.setMinutes(bookedSince.getMinutes() - NO_SHOW_M);

  return api.loadFlooreManagerAppointments(
    {
      now: d.toString(new Date()),
      location_id,
      filter: {
        start: {
          gte: d.toString(start),
          lt: d.toString(end),
        },
        duration: {
          neq: 0,
        },
        location_id: { eq: location_id },
        or: [
          {
            status_id: {
              eq: status.BOOKED,
            },
            start: {
              gte: d.toString(bookedSince),
            },
          },
          {
            status_id: {
              in: Array.from(dissoc(interestingStatuses, status.BOOKED)),
            },
          },
          {
            parent_id: { null: false },
          },
        ],
      },
      order: { asc: "start" },
      auditFilter: {
        or: [
          {
            action: { eq: "I" },
          },
          {
            changed_fields: {
              name: "status_id",
              expr: { null: false },
            },
          },
          {
            changed_fields: {
              name: "active_step",
              expr: { null: false },
            },
          },
        ],
      },
      blocationFilter: {
        valid_from: {
          lte: d.toDateString(start),
        },
        valid_to: {
          gte: d.toDateString(end),
        },
        location_id: { eq: location_id },
        or: [
          {
            days: {
              array_overlaps: [dateToDay(start)],
            },
          },
          {
            days: {
              array_empty: true,
            },
          },
        ],
      },
    },
    api.contextFromLocation(location_id),
  );
}

function appointmentToEvent(appointment) {
  return {
    ...appointment,
    title: eventType.APPOINTMENT,
    resourceId: appointment.calendar_id,
    start: new Date(appointment.start),
    end: new Date(appointment.end),
    extendedProps: appointment,
  };
}

/**
 * @param {HTMLElement} el
 * @param {string} appointmentId
 *
 * @returns {HTMLElement}
 */
function ghostFrom(el, appointmentId) {
  const bcr = el.getBoundingClientRect();

  /** @type {HTMLElement} */
  const ghost = el.cloneNode(true);
  ghost.style.width = `${bcr.width}px`;
  ghost.style.height = `${bcr.height}px`;
  ghost.style.zIndex = 10000;
  ghost.style.position = "absolute";
  ghost.style.pointerEvents = "none";
  ghost.style.top = `${-bcr.height}px`;
  ghost.style.left = `${-bcr.width}px`;
  ghost.style.right = "unset";

  ghost
    .querySelectorAll(
      `.appointment-draggable:not([data-appointment-id="${appointmentId}"])`,
    )
    .forEach((el) => (el.style.display = "none"));

  return ghost;
}

/**
 * @param {DataTransfer} dataTransfer
 * @param {Service} service
 */
function setServiceTransferData(dataTransfer, service, data, groupId) {
  serviceFormatList(service, groupId).forEach((format) => {
    dataTransfer.setData(format, data);
  });
}

const selectedAppointments = { groupId: null, appointments: new Set() };

function getSelectedAppointmentIds(appointmentId, appointmentGroupId) {
  if (
    appointmentGroupId == null ||
    appointmentGroupId !== selectedAppointments.groupId
  ) {
    return [appointmentId];
  }

  return Array.from(selectedAppointments.appointments);
}

function clearSelectedAppointments(appointmentGroupId) {
  selectedAppointments.groupId = appointmentGroupId;
  selectedAppointments.appointments = new Set();
}

function setSelectedAppointment(appointmentId, appointmentGroupId, selected) {
  if (appointmentGroupId == null) {
    return;
  }

  if (selectedAppointments.groupId !== appointmentGroupId) {
    clearSelectedAppointments(appointmentGroupId);
  }

  if (selected) {
    selectedAppointments.appointments.add(appointmentId);
  } else {
    selectedAppointments.appointments.delete(appointmentId);
  }
}

function setReservationOverlay(reservation_id) {
  reservationOverlay = reservation_id != null;
  document
    .querySelectorAll(`.calendar-event--reservation-overlay`)
    .forEach((el) => {
      el.classList.add("hidden");
    });

  if (reservation_id != null) {
    document
      .querySelectorAll(
        `.calendar-event--reservation-overlay--${reservation_id}`,
      )
      .forEach((el) => {
        el.classList.remove("hidden");
      });
  }
}

const invisibleActiveSteps = new Set([
  appointmentStep.APPOINTMENT_FINISHED,
  appointmentStep.CHAIR_CLEANING,
]);

/**
 * @param {object} opts
 * @param {HTMLElement} opts.el
 * @param {React.RefObject<string | undefined>} opts.location_idRef
 * @param {React.RefObject<number>} opts.selectedTime_ref
 * @param {React.RefObject<Buffer[]>} opts.buffers_ref
 * @param {(event: object | null) => void} opts.setEditEvent
 * @param {(Appointment[]) => void} opts.setAdhoc
 */
function createEcal({
  el,
  setEditEvent,
  location_idRef,
  selectedTime_ref,
  buffers_ref,
  setAdhoc,
  setAppointmentsByCalendarId,
  setAppointmentById,
  eventContentFn,
  setHoveredReservationId,
}) {
  const clickHandler = createClickHandler({
    onClick: (info) => {
      const target = info.jsEvent.target;
      if (
        target.tagName === "INPUT" ||
        info.event.extendedProps?.parent_id != null
      ) {
        return;
      }

      setEditEvent([info.event.extendedProps]);
    },
    onDoubleClick: (info) => {
      const clientId = info.event.extendedProps?.client?.id;
      if (clientId == null) {
        return;
      }

      window.open(
        createUrlString(
          location.origin,
          location.search,
          `/clients/${clientId}`,
        ),
        "_blank",
      );
    },
  });

  function onchange(e) {
    const target = e.target;

    const { appointmentId, appointmentGroupId } = target.dataset;
    setSelectedAppointment(appointmentId, appointmentGroupId, target.checked);
  }

  let ghost = null;

  function ondragstart(e) {
    if (!e.target.classList.contains("appointment-draggable")) {
      return;
    }

    const dataset = e.target.dataset;
    const eventId = dataset.appointmentId;
    const service = JSON.parse(dataset.service);
    const groupId = dataset.appointmentGroupId;

    const el = e.target.closest("article.ec-event");
    ghost = ghostFrom(el, eventId);
    document.body.append(ghost);
    e.dataTransfer.setDragImage(ghost, 0, 0);
    el.classList.add("dragging");

    e.dataTransfer.dropEffect = "move";
    setServiceTransferData(
      e.dataTransfer,
      service,
      JSON.stringify({
        appointment_ids: getSelectedAppointmentIds(eventId, groupId),
        appointment_group_id: groupId,
      }),
      groupId,
    );
    clearSelectedAppointments(null);

    dragging = true;
  }

  function ondragend(e) {
    const el = e.target.closest("article.ec-event");
    el.classList.remove("dragging");
    ghost?.remove();
    ghost = null;

    dragging = false;
  }

  el.addEventListener("dragstart", ondragstart);
  el.addEventListener("dragend", ondragend);
  el.addEventListener("change", onchange);
  el.addEventListener("mouseover", function (e) {
    overInput = e.relatedTarget?.tagName === "INPUT";
  });

  /** @type {EventCalendar} */
  const cal = createCalendar(el, [ResourceTimeGrid, Interaction], {
    view: "resourceTimeGridDay",
    nowIndicator: true,
    headerToolbar: { start: "", center: "", end: "" },
    slotLabelFormat: { timeStyle: "short", hour12: false },
    allDaySlot: false,
    slotMinTime: "24:00",
    slotMaxTime: "00:00",
    flexibleSlotTimeLimits: true,
    scrollTime: "09:00",
    eventTextColor: "#000",
    eventDurationEditable: false,
    eventStartEditable: false,
    slotHeight: 160,
    eventClassNames: (info) => {
      const appointment = info.event.extendedProps;

      return `event-id-${info.event.id} status-${appointment.status_id}`;
    },
    eventContent: eventContentFn,
    eventClick: (info) => {
      if (info.event.title !== eventType.APPOINTMENT) {
        return;
      }

      clickHandler(info);
    },
    eventMouseEnter: ({ event }) => {
      const reservation_id = event.extendedProps.reservation_id;
      setHoveredReservationId(reservation_id);
      setReservationOverlay(reservation_id);
    },
    eventMouseLeave: () => {
      setHoveredReservationId(null);
      setReservationOverlay(null);
    },
    eventAllUpdated: () => {
      const ecExtra = document.querySelector(".ec-now-indicator");
      ecExtra?.setAttribute("data-now-time", time24(new Date()));
    },
    eventSources: [
      {
        events: async function (fetchInfo) {
          const location_id = location_idRef.current;
          const selectedTime = selectedTime_ref.current ?? 0;
          if (location_id == null) {
            return [];
          }

          const start = fetchInfo.start;
          const end =
            selectedTime === 0
              ? fetchInfo.end
              : (() => {
                  const end = new Date();
                  end.setHours(end.getHours() + selectedTime);

                  return new Date(
                    Math.min(fetchInfo.end.getTime(), end.getTime()),
                  );
                })();

          const resp = await fetchAppointments(start, end, location_id);

          const appointments = resp.data.appointments.data;
          const appointmentById = indexBy(appointments, (a) => a.id);
          const childAppointments = groupBy(appointments, (a) => a.parent_id);
          delete childAppointments[null];

          for (const id in childAppointments) {
            const parent = appointmentById[id];
            if (parent == null) {
              continue;
            }
            parent.children = childAppointments[id];
          }

          setAppointmentById(appointmentById);
          setAppointmentsByCalendarId(
            getAppointmentsByCalendarId(appointments),
          );

          const byAdhoc = groupBy(appointments, (a) => a.calendar_id == null);
          const adhoc = (byAdhoc[true] ?? []).filter(
            (a) =>
              interestingStatuses.has(a.status_id) &&
              !invisibleActiveSteps.has(a.active_step),
          );
          setAdhoc(adhoc);

          const appointmentsRes = (byAdhoc[false] ?? [])
            .filter(
              (a) =>
                (a.parent_id == null || appointmentById[a.parent_id] != null) &&
                !invisibleActiveSteps.has(a.active_step),
            )
            .map((a) => appointmentToEvent(a));

          const todayDate = d.toDateString(fetchInfo.start);

          const blocationsFromMin = appointmentsRes.reduce(
            (acc, next) => d.minDate(acc, next.start),
            d.endOfDay(start),
          );

          const blocationsToMax = appointmentsRes.reduce(
            (acc, next) => d.maxDate(acc, next.start),
            d.startOfDay(end),
          );

          const blocations = resp.data.locationBlocations.data.flatMap(
            (blocation) => {
              return blocation.calendars.map((c) => {
                const event = {
                  title: eventType.BLOCATION,
                  start: d.maxDate(
                    blocationsFromMin,
                    new Date(`${todayDate} ${blocation.from}`),
                  ),
                  end: d.minDate(
                    blocationsToMax,
                    new Date(`${todayDate} ${blocation.to}`),
                  ),
                  resourceId: c.calendar_id,
                  extendedProps: blocation,
                };

                return event;
              });
            },
          );

          const buffers = buffers_ref.current.map((b) => {
            const event = {
              title: eventType.BUFFER,
              start: d.maxDate(blocationsFromMin, new Date(b.from)),
              end: d.minDate(blocationsToMax, new Date(b.to)),
              resourceId: b.calendar_id,
              extendedProps: b,
            };

            return event;
          });

          return [...buffers, ...blocations, ...appointmentsRes];
        },
      },
    ],
  });

  return cal;
}

/**
 * @param {object} msg
 * @param {string} location_id
 * @param {string} date
 *
 * @returns {boolean}
 */
function isInterestingNotification(msg, location_id, date) {
  switch (msg.type) {
    case "__connect":
      return true;
    case "appointment_changed": {
      const { location_ids, days } = msg.payload;

      if (Object.keys(msg.payload).length === 0) {
        return true;
      }

      return location_ids?.includes(location_id) && days.includes(date);
    }
  }

  return false;
}

const Cal = React.memo(function Cal({
  date,
  setEditEvent,
  location_id,
  selectedTime,
  setAdhoc,
  appointmentsByCalendarId,
  setAppointmentsByCalendarId,
  setAppointmentById,
  resources,
  cal,
  buffers,
  setHoveredReservationId,
}) {
  const ecalEl = React.useRef();
  const location_idRef = React.useRef();
  const buffers_ref = React.useRef([]);
  const selectedTime_ref = React.useRef(selectedTime);
  const resourcesById = React.useMemo(
    () => indexBy(resources, (r) => r.id),
    [resources],
  );
  const cache = React.useRef({});

  React.useEffect(() => {
    const refresh = throttle(() => {
      cal.current?.refetchEvents();
    }, THROTTLE_REFRESH);

    function maybeRefresh(msg) {
      if (isInterestingNotification(msg, location_idRef.current, date)) {
        refresh();
      }
    }

    ws.subscribe(maybeRefresh);

    return () => {
      ws.unsubscribe(maybeRefresh);
    };
  }, []);

  React.useEffect(() => {
    location_idRef.current = location_id;
    selectedTime_ref.current = selectedTime;
    buffers_ref.current = buffers;
    cal.current?.refetchEvents();
  }, [location_id, selectedTime, buffers]);

  React.useEffect(() => {
    cal.current = createEcal({
      el: ecalEl.current,
      setEditEvent,
      location_idRef,
      selectedTime_ref,
      buffers_ref,
      setAdhoc,
      setAppointmentsByCalendarId,
      setAppointmentById,
      eventContentFn: createEventContentFn(
        cache.current,
        resourcesById,
        appointmentsByCalendarId,
        new Date(),
      ),
      setHoveredReservationId: setHoveredReservationId,
    });

    return function () {
      destroyCalendar(cal.current);
      cal.current = null;
    };
  }, []);

  React.useEffect(() => {
    cache.current = {};
    const c = cal.current;
    if (c == null) {
      return;
    }

    const update = () => {
      c.setOption(
        "eventContent",
        createEventContentFn(
          cache.current,
          resourcesById,
          appointmentsByCalendarId,
          new Date(),
        ),
      );
    };

    const controller = new AbortController();
    update();
    animationInterval(1000, controller.signal, () => {
      if (!dragging && !overInput && !reservationOverlay) {
        update();
      }
    });

    return function () {
      controller.abort();
    };
  }, [appointmentsByCalendarId]);

  React.useEffect(() => {
    if (cal.current == null) {
      return;
    }

    cal.current.setOption("resources", resources);
  }, [resources]);

  React.useEffect(() => {
    const c = cal.current;
    if (c == null) {
      return;
    }

    c.setOption("date", date);
  }, [date]);

  return (
    <>
      <div id="ecal" className="floor-manager-calendar" ref={ecalEl} />
    </>
  );
});

function initialAssignCalendarDialogState() {
  return {
    loading: false,
    errors: [],
  };
}

function getAssignableState(appointments) {
  if (appointments[0]?.service?.type === "consultation") {
    return {
      status_id: status.TREATING,
      active_step: appointmentStep.COMPLETION,
    };
  }

  return { status_id: status.ASSIGNED };
}

/**
 * @param {object} props
 * @param {(data?: AssignCalendar) => void} props.setAssignCalendar
 * @param {AssignCalendar} props.assignCalendar
 * @param {Record<string, Appointment>} props.appointmentById
 */
function AssignCalendarDialog({
  setAssignCalendar,
  assignCalendar,
  appointmentById,
}) {
  const appointments = assignCalendar.appointment_ids
    .map((id) => appointmentById[id])
    .filter((a) => a != null);
  const assignableState = getAssignableState(appointments);
  const assignableRoles = React.useMemo(
    () => getAssignableRoles(appointments),
    [],
  );
  const [state, setState] = React.useState(initialAssignCalendarDialogState());
  const { t } = useTranslation();
  const { options, loading } = useNurseOptionsGrouped(assignableRoles);

  const theme = useTheme();

  return (
    <Dialog open={true} onClose={() => setAssignCalendar(null)} maxWidth="xs">
      <DialogTitle>
        {state.loading && (
          <CircularProgress
            size={24}
            sx={{ position: "absolute", left: "calc(50% - 24px)" }}
          />
        )}
        Přiřazení personálu
        <IconButton aria-label="close" onClick={() => setAssignCalendar(null)}>
          <FontAwesomeIcon icon={faXmark} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <CircularProgress size={24} />
        ) : (
          <>
            {appointments.map((a) => {
              return (
                <Box
                  key={a.id}
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "1rem",
                    mb: 2,
                    pb: 2,
                    borderBottom: "1px solid",
                    borderBottomColor: "divider",
                  }}
                >
                  <Box>
                    <FontAwesomeIcon
                      size="lg"
                      color={theme.palette.primary.main}
                      icon={faChairOffice}
                      style={{ fontSize: "2rem", marginRight: "1rem" }}
                    />
                    {`Zdroj ${assignCalendar.actual_calendar.title}`}
                  </Box>
                  <Box sx={{ ml: 3 }}>
                    <FontAwesomeIcon
                      size="lg"
                      color={theme.palette.primary.main}
                      icon={faUser}
                      style={{ fontSize: "2rem", marginRight: "1rem" }}
                    />
                    {f.fullName(a.client)}
                  </Box>
                </Box>
              );
            })}

            <FormControl required disabled={state.loading}>
              <RadioGroup
                onChange={async (_, value) => {
                  setState((state) => ({ ...state, loading: true }));
                  const result = await api.patchAppointments(
                    assignCalendar.appointment_ids.map((id) => ({
                      id: id,
                      actual_calendar_id: assignCalendar.actual_calendar.id,
                      employee_id: value,
                      ...assignableState,
                    })),
                  );
                  const errors = errorsFromApi({ result, t });
                  setState((state) => ({ ...state, errors: errors }));
                  if (errors.length === 0) {
                    setAssignCalendar(null);
                  }
                  setState((state) => ({ ...state, loading: false }));
                }}
              >
                <NurseGroup
                  label={employeeStatusToLabel.available}
                  nurses={options.available}
                />
                <NurseGroup
                  label={employeeStatusToLabel.assigned}
                  nurses={options.assigned}
                />
                <NurseGroup
                  label={employeeStatusToLabel.unavailable}
                  nurses={options.unavailable}
                />
              </RadioGroup>
            </FormControl>
          </>
        )}
        <FormErrorList formErrors={state.errors} />
      </DialogContent>
    </Dialog>
  );
}

const hourRange = [2, 3, 4];

const Toolbar = React.memo(function Toolbar({
  setState,
  selectedTime,
  date,
  dateOptions,
}) {
  const { t } = useTranslation();
  const setSelectedTime = (selectedTime) =>
    setState((state) => ({ ...state, selectedTime }));
  const setSelectedDate = (date) => setState((state) => ({ ...state, date }));

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
        alignItems: "flex-end",
        mb: 0.5,
      }}
    >
      <Typography variant="h3" gutterBottom sx={{ userSelect: "none" }}>
        {t("fcalendar.planned")}
      </Typography>

      <Box display="flex" gap={2}>
        <Select
          value={date}
          onChange={(e) => {
            setSelectedDate(e.target.value);
          }}
        >
          {dateOptions.map((o) => {
            return (
              <MenuItem key={o} value={o}>
                {f.date(o)}
              </MenuItem>
            );
          })}
        </Select>
        <FormControl>
          <Select
            value={selectedTime}
            onChange={(e) => {
              const selectedTime = e.target.value;
              setSelectedTime(selectedTime);
            }}
          >
            {hourRange.map((h) => (
              <MenuItem key={h} value={h}>
                {t("fcalendar.hour_range", { count: h })}
              </MenuItem>
            ))}
            <MenuItem value={0}>{t("fcalendar.hour_rangeAll")}</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
});

/**
 * @param {object} props
 * @param {Appointment[]} props.adhoc
 * @param {string} props.location_id
 * @param {Calendar[]} resources
 * @param {Record<string, Appointment[]} appointmentsByCalendarId
 */
function AdhocAppointments({
  adhoc,
  location_id,
  setEditEvent,
  resources,
  appointmentsByCalendarId,
  setHoveredReservationId,
}) {
  const resourceById = indexBy(resources, (r) => r.id);
  const appointments = adhoc.filter((a) => a.location_id === location_id);
  const [now, setNow] = React.useState(new Date());
  React.useEffect(() => {
    const controller = new AbortController();
    animationInterval(1000, controller.signal, () => {
      setNow(new Date());
    });

    return function () {
      controller.abort();
    };
  });

  return (
    <Box
      display="grid"
      gridTemplateColumns={`repeat(${resources.length}, minmax(0, 1fr))`}
      rowGap={1}
      width="100%"
    >
      {appointments.map((appointment) => {
        const draggable =
          appointment.status_id === status.WAITING &&
          appointment.actual_calendar_id == null;
        const client = appointment.client;
        const service = appointment.service;
        const clickHandler = createClickHandler({
          onClick: () => {
            setEditEvent([appointment]);
          },
          onDoubleClick: () => {
            const clientId = appointment.client?.id;
            if (clientId == null) {
              return;
            }

            window.open(
              createUrlString(
                location.origin,
                location.search,
                `/clients/${clientId}`,
              ),
              "_blank",
            );
          },
        });

        const times = adhocWaitingTimesIcons(rawWaitingTimes(appointment, now));

        return (
          <Box
            key={appointment.id}
            className="floor-manager-resource"
            onClick={clickHandler}
            sx={{
              cursor: "pointer",
              backgroundColor: "var(--ec-event-bg-color)",
              display: "flex",
              flexGrow: "1",
              flexShrink: "1",
              position: "relative",
            }}
            draggable={draggable}
            onDragStart={(e) => {
              if (e.target !== e.currentTarget) {
                return;
              }

              const el = e.currentTarget;
              e.dataTransfer.setDragImage(el, 0, 0);
              el.classList.add("dragging");
              e.dataTransfer.dropEffect = "move";
              setServiceTransferData(
                e.dataTransfer,
                appointment.service,
                JSON.stringify({
                  appointment_ids: getSelectedAppointmentIds(
                    appointment.id,
                    appointment.group_id,
                  ),
                  appointment_group_id: appointment.group_id,
                }),
                appointment.group_id,
              );
            }}
            onDragEnd={(e) => {
              const el = e.currentTarget;
              el.classList.remove("dragging");
            }}
            onMouseEnter={() => {
              setHoveredReservationId(appointment.reservation_id);
              setReservationOverlay(appointment.reservation_id);
            }}
            onMouseLeave={() => {
              setHoveredReservationId(null);
              setReservationOverlay(null);
            }}
          >
            <TagList appointments={[appointment]} />
            <Box
              data-id={appointment.id}
              className={`calendar-adhoc-appointment status-${appointment.status_id}`}
              sx={{
                cursor: "pointer",
                border: `1px solid ${appointmentColor(appointment)}`,
                background: appointmentBackground(appointment),
                padding: 0.5,
              }}
            >
              <AssignedEvent
                resources={resourceById}
                appointment={appointment}
                appointmentsByCalendarId={appointmentsByCalendarId}
              />
              <ReservationOverlay appointment={appointment} />
              {client && (
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: "0.2rem" }}
                >
                  <VirtualCareTag client={client} />
                  <Box
                    sx={{ color: "_components.heading.color", fontWeight: 600 }}
                    className="single-line"
                  >
                    {f.fullName(client)}
                  </Box>
                </Box>
              )}
              <ServiceTag service={service} />
              <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                <Typography
                  sx={{
                    fontSize: "1.1rem",
                    color: "_components.alert.info.color",
                  }}
                >
                  {times?.waiting_minutes}
                </Typography>
                <Chip
                  label={adhocTypes[appointment.adhoc_type]?.name}
                  color="primary"
                  size="small"
                  sx={{
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    height: "1.6rem",
                    "& .MuiChip-label": {
                      paddingLeft: "0.6rem",
                      paddingRight: "0.6rem",
                    },
                  }}
                  className={"single-line"}
                />
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

function initialCalendarState() {
  const today = f.dateInput(new Date());
  return {
    today: today,
    date: today,
    editEvent: null,
    assignCalendar: null,
    selectedTime: 0,
    adhoc: [],
    appointmentsByCalendarId: {},
    appointmentById: {},
    hoveredReservation_id: null,
  };
}

/**
 * @param {Date} from
 * @param {Date} to
 */
function* days(from, to) {
  if (to < from) {
    return;
  }

  let currentFrom = new Date(from.getTime());
  while (currentFrom < to) {
    yield currentFrom;

    const next = new Date(currentFrom.getTime());
    next.setDate(next.getDate() + 1);
    currentFrom = next;
  }
}

function getBlocationDays(location) {
  return location.blocations
    .filter((b) => b.exceptional)
    .flatMap((b) => {
      const days2 = Array.from(
        days(d.parseDateTime(b.valid_from), d.parseDateTime(b.valid_to)),
      );

      const validDays = new Set(b.days);
      if (validDays.size === 0) {
        return days2;
      }

      return days2.filter((d) => validDays.has(dateToDay(d)));
    })
    .map((date) => f.dateInput(date));
}

function getClosureDays(location) {
  return location.closures.flatMap((c) =>
    Array.from(
      days(d.parseDateTime(c.valid_from), d.parseDateTime(c.valid_to)),
    ).map((date) => f.dateInput(date)),
  );
}

function getDateOptions(location, today) {
  return Array.from(
    new Set([
      ...getBlocationDays(location),
      ...getClosureDays(location),
      today,
    ]),
  )
    .filter((d) => d >= today)
    .sort();
}

export default function Dashboard() {
  const { t } = useTranslation();

  const loaderData = useLoaderData();
  const resources = loaderData.data.calendars.data;
  const location = loaderData.data.location;
  const buffers = location.buffers;

  const [state, setState] = React.useState(initialCalendarState);
  const dateOptions = React.useMemo(
    () => getDateOptions(location, state.today),
    [location, state.today],
  );

  const cal = React.useRef();
  const location_id = useCurrentLocation();

  const { date, editEvent, assignCalendar } = state;
  const setEditEvent = React.useCallback(
    (editEvent) => setState((state) => ({ ...state, editEvent })),
    [setState],
  );
  const setAssignCalendar = React.useCallback(
    (assignCalendar) => setState((state) => ({ ...state, assignCalendar })),
    [setState],
  );
  const setAdhoc = React.useCallback(
    (adhoc) => setState((state) => ({ ...state, adhoc })),
    [setState],
  );
  const setAppointmentsByCalendarId = React.useCallback(
    (appointmentsByCalendarId) =>
      setState((state) => ({ ...state, appointmentsByCalendarId })),
    [setState],
  );
  const setAppointmentById = React.useCallback(
    (appointmentById) => setState((state) => ({ ...state, appointmentById })),
    [setState],
  );
  const setHoveredReservationId = React.useCallback(
    (reservation_id) =>
      setState((state) => ({
        ...state,
        hoveredReservation_id: reservation_id,
      })),
    [setState],
  );

  return (
    <>
      <Box
        sx={{
          position: "sticky",
          top: "var(--app-sticky-top)",
          zIndex: "1010",
          backgroundColor: "background.default",
        }}
      >
        <NowProvider interval={1000}>
          <ResourceList
            leftMargin
            resources={resources}
            setAssignCalendar={setAssignCalendar}
            setEditEvent={setEditEvent}
            appointmentsByCalendarId={state.appointmentsByCalendarId}
          />
        </NowProvider>
        <Box className="floor-manager-calendar" sx={{ mt: 2, display: "flex" }}>
          <LeftMargin />
          <Box
            display="flex"
            flexGrow="1"
            flexShrink="1"
            flexDirection="column"
          >
            {state.adhoc.length > 0 && (
              <>
                <Typography variant="h3" sx={{ mb: 1.5, userSelect: "none" }}>
                  {t("fcalendar.adhoc")}
                </Typography>
                <Box display="flex" flexGrow="1" flexShrink="1">
                  <AdhocAppointments
                    adhoc={state.adhoc}
                    location_id={location_id}
                    setEditEvent={setEditEvent}
                    resources={resources}
                    appointmentsByCalendarId={state.appointmentsByCalendarId}
                    setHoveredReservationId={setHoveredReservationId}
                  />
                </Box>
              </>
            )}
          </Box>
        </Box>
        <Box
          sx={{
            my: 1,
            pr: 1,
            pb: 1,
            display: "flex",
            backgroundColor: "background.default",
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              height: "3px",
              boxShadow: "0 3px 4px 0 rgba(0, 0, 0, 0.45)",
              zIndex: -1,
            },
          }}
        >
          <LeftMargin />
          <Toolbar
            setState={setState}
            date={date}
            selectedTime={state.selectedTime}
            dateOptions={dateOptions}
          />
        </Box>
      </Box>
      <Cal
        location_id={location_id}
        selectedTime={state.selectedTime}
        date={date}
        setEditEvent={setEditEvent}
        setAssignCalendar={setAssignCalendar}
        setAdhoc={setAdhoc}
        appointmentsByCalendarId={state.appointmentsByCalendarId}
        setAppointmentsByCalendarId={setAppointmentsByCalendarId}
        setAppointmentById={setAppointmentById}
        resources={resources}
        cal={cal}
        buffers={buffers}
        setHoveredReservationId={setHoveredReservationId}
      />

      {editEvent && (
        <AppointmentDetailDialog
          appointmentList={editEvent}
          handleCloseAppointmentDetailDialog={() => setEditEvent(null)}
          openAppointmentDetailDialog={!!setEditEvent}
        />
      )}

      {assignCalendar && (
        <AssignCalendarDialog
          setAssignCalendar={setAssignCalendar}
          assignCalendar={assignCalendar}
          appointmentById={state.appointmentById}
        />
      )}
    </>
  );
}
